import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getAdminSettlement,
  settleAdminSettlement,
  updateAdminSettlement
} from "../api/adminSettlements";
import { BACKEND_URL } from "../api/api";

const formatDate = (value) => value ? new Date(value).toLocaleString("nl-BE") : "-";
const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;
const numberValue = (value) => value === "" ? 0 : Number(value || 0);

export default function AdminSettlementDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    damageCost: "",
    missingAccessoryCost: "",
    cleaningCost: "",
    otherCost: "",
    reason: "",
    notes: ""
  });
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const { data: settlement, isLoading, isError } = useQuery({
    queryKey: ["admin", "settlement", id],
    queryFn: () => getAdminSettlement(id)
  });

  useEffect(() => {
    if (!settlement) return;
    setForm({
      damageCost: settlement.damageCost ?? "",
      missingAccessoryCost: settlement.missingAccessoryCost ?? "",
      cleaningCost: settlement.cleaningCost ?? "",
      otherCost: settlement.otherCost ?? "",
      reason: settlement.reason || "",
      notes: settlement.notes || ""
    });
  }, [settlement]);

  const totals = useMemo(() => {
    const amountWithheld = numberValue(form.damageCost) + numberValue(form.missingAccessoryCost) + numberValue(form.cleaningCost) + numberValue(form.otherCost);
    return {
      amountWithheld,
      amountReturned: Math.max(Number(settlement?.depositAmount || 0) - amountWithheld, 0)
    };
  }, [form, settlement]);

  const readonly = settlement?.status === "SETTLED";
  const invalidReason = totals.amountWithheld > 0 && !form.reason.trim();
  const invalidTotal = totals.amountWithheld > Number(settlement?.depositAmount || 0);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "settlements"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "settlement", id] });
    queryClient.invalidateQueries({ queryKey: ["reservations"] });
  };

  const updateMutation = useMutation({
    mutationFn: () => updateAdminSettlement(id, form),
    onSuccess: () => {
      invalidate();
      toast.success("Waarborg opgeslagen");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Waarborg opslaan mislukt")
  });

  const settleMutation = useMutation({
    mutationFn: async () => {
      await updateAdminSettlement(id, form);
      return settleAdminSettlement(id);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Waarborg afgehandeld");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Waarborg afhandelen mislukt")
  });

  if (isLoading) return <div className="loading-state">Waarborg laden...</div>;

  if (isError || !settlement) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Waarborg niet gevonden</h1>
          <Link className="button secondary" to="/admin/settlements">Terug naar waarborgen</Link>
        </div>
      </section>
    );
  }

  const reservation = settlement.reservation;
  const customer = settlement.customer || reservation?.customer;
  const trailer = settlement.trailer || reservation?.trailer;
  const checkin = settlement.checkInInspection;
  const optionIssues = checkin?.optionChecks?.filter((item) => item.status !== "OK") || [];
  const checkinPhotos = checkin?.photos || [];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Waarborg #{settlement.id}</h1>
          <p className="page-subtitle">{settlement.status === "SETTLED" ? `Afgehandeld op ${formatDate(settlement.settledAt)}` : "Nog af te handelen"}</p>
        </div>
        <Link className="button secondary" to="/admin/settlements">Terug</Link>
      </header>

      <div className="trailer-detail-layout">
        <section className="panel panel-pad">
          <div className="detail-section">
            <h2 className="detail-title">Context</h2>
            <div className="detail-grid">
              <div className="detail-item"><div className="detail-label">Reservatie</div><div className="detail-value">#{settlement.reservationId}</div></div>
              <div className="detail-item"><div className="detail-label">Klant</div><div className="detail-value">{customer?.firstName || reservation?.firstName || ""} {customer?.lastName || reservation?.name || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Trailer</div><div className="detail-value">{trailer?.name || "-"}</div></div>
              <div className="detail-item"><div className="detail-label">Check-in</div><div className="detail-value">{formatDate(checkin?.confirmedAt || reservation?.checkInAt)}</div></div>
              <div className="detail-item"><div className="detail-label">Issues</div><div className="detail-value">{checkin?.hasIssues ? "Ja" : "Nee"}</div></div>
              <div className="detail-item"><div className="detail-label">Nieuwe schade</div><div className="detail-value">{checkin?.hasDamage ? "Ja" : "Nee"}</div></div>
            </div>
            {checkin?.damageDescription && <p className="error-text">{checkin.damageDescription}</p>}
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Afhandeling</h2>
            <div className="form-grid">
              <label className="field"><span className="field-label">Waarborgbedrag</span><input className="input" value={money(settlement.depositAmount)} readOnly /></label>
              <label className="field"><span className="field-label">Schadebedrag</span><input className="input" type="number" min="0" step="0.01" value={form.damageCost} readOnly={readonly} onChange={(e) => setForm({ ...form, damageCost: e.target.value })} /></label>
              <label className="field"><span className="field-label">Ontbrekende accessoires</span><input className="input" type="number" min="0" step="0.01" value={form.missingAccessoryCost} readOnly={readonly} onChange={(e) => setForm({ ...form, missingAccessoryCost: e.target.value })} /></label>
              <label className="field"><span className="field-label">Reinigingskost</span><input className="input" type="number" min="0" step="0.01" value={form.cleaningCost} readOnly={readonly} onChange={(e) => setForm({ ...form, cleaningCost: e.target.value })} /></label>
              <label className="field"><span className="field-label">Andere kost</span><input className="input" type="number" min="0" step="0.01" value={form.otherCost} readOnly={readonly} onChange={(e) => setForm({ ...form, otherCost: e.target.value })} /></label>
              <label className="field"><span className="field-label">Totaal inhouding</span><input className="input" value={money(totals.amountWithheld)} readOnly /></label>
              <label className="field"><span className="field-label">Terug te geven</span><input className="input" value={money(totals.amountReturned)} readOnly /></label>
            </div>
            <label className="field" style={{ marginTop: 12 }}>
              <span className="field-label">Reden</span>
              <input className="input" value={form.reason} readOnly={readonly} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              {invalidReason && <span className="field-error">Reden is verplicht wanneer er een bedrag wordt ingehouden.</span>}
              {invalidTotal && <span className="field-error">Inhouding kan niet hoger zijn dan de waarborg.</span>}
            </label>
            <label className="field" style={{ marginTop: 12 }}>
              <span className="field-label">Notities</span>
              <textarea className="textarea" value={form.notes} readOnly={readonly} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
            {!readonly && (
              <div className="action-row reservation-action-row" style={{ marginTop: 12 }}>
                <button className="button secondary" disabled={updateMutation.isPending || invalidReason || invalidTotal} onClick={() => updateMutation.mutate()}>
                  {updateMutation.isPending ? "Opslaan..." : "Opslaan"}
                </button>
                <button className="button" disabled={settleMutation.isPending || invalidReason || invalidTotal} onClick={() => settleMutation.mutate()}>
                  {settleMutation.isPending ? "Afhandelen..." : "Markeer als afgehandeld"}
                </button>
              </div>
            )}
          </div>
        </section>

        <aside className="panel panel-pad detail-panel">
          <div className="detail-section">
            <h2 className="detail-title">Optie/accessoire issues</h2>
            {optionIssues.length === 0 && <div className="empty-state">Geen optie-issues gevonden.</div>}
            {optionIssues.map((item) => (
              <button key={item.id} type="button" className="list-row reservation-row" onClick={() => item.photoUrl && setPreviewPhoto({ imageUrl: item.photoUrl, label: item.optionName })}>
                <div>
                  <div className="row-title">{item.optionName}</div>
                  <div className="row-meta">{item.status} {item.notes ? `- ${item.notes}` : ""}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Check-in foto's</h2>
            {checkinPhotos.length === 0 && <div className="empty-state">Geen check-in foto's gevonden.</div>}
            <div className="photo-grid">
              {checkinPhotos.map((photo) => (
                <button key={photo.id} type="button" className="checkout-admin-photo" onClick={() => setPreviewPhoto(photo)}>
                  <img className="photo-thumb" src={`${BACKEND_URL}${photo.imageUrl}`} alt={photo.label} />
                  <span>{photo.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h2 className="detail-title">Schadegevallen</h2>
            {settlement.damageCases?.length === 0 && <div className="empty-state">Geen schadegevallen gevonden.</div>}
            {settlement.damageCases?.map((damageCase) => (
              <div key={damageCase.id} className="checkout-damage">
                <span className="badge danger">{damageCase.status}</span>
                <p className="page-subtitle">{damageCase.description || "Geen omschrijving"}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {previewPhoto && (
        <button className="image-modal" onClick={() => setPreviewPhoto(null)}>
          <span className="modal-close" aria-hidden="true">Sluiten</span>
          <figure className="checkout-photo-modal" onClick={(event) => event.stopPropagation()}>
            <img src={`${BACKEND_URL}${previewPhoto.imageUrl}`} alt={previewPhoto.label} />
            <figcaption>{previewPhoto.label || previewPhoto.type}</figcaption>
          </figure>
        </button>
      )}
    </section>
  );
}
